import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import clipoidLogo from '../../assets/clipoid_logo.png';
import { CardSpotlight } from '../components/aceternity/CardSpotlight';

function OAuthRegisterPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Get registration data from URL params
  const provider = searchParams.get('provider');
  const message = searchParams.get('message');
  const registrationToken = searchParams.get('registrationToken') || searchParams.get('token');
  
  // Parse registration data from URL params
  const [registrationData, setRegistrationData] = useState<{
    provider: string;
    providerId: string;
    email?: string;
    name?: string;
    username?: string;
    profileImageUrl?: string;
  } | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
      return;
    }

    // Get registration data from URL params
    if (provider) {
      const data = {
        provider: provider,
        providerId: searchParams.get('providerId') || '',
        email: searchParams.get('email') || undefined,
        name: searchParams.get('name') || undefined,
        username: searchParams.get('username') || undefined,
        profileImageUrl: searchParams.get('profileImageUrl') || undefined,
      };
      setRegistrationData(data);
    } else if (!registrationToken) {
      setError('Missing registration data. Please try logging in again.');
    }
  }, [isAuthenticated, navigate, provider, searchParams, registrationToken]);

  const handleCreateAccount = async () => {
    if (!registrationData || !registrationToken) {
      setError('Missing registration data. Please try logging in again.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Call the create-from-oauth endpoint with discoveryToken
      const response = await api.post('/auth/create-from-oauth', {
        discoveryToken: registrationToken,  // Backend expects 'discoveryToken'
      });

      if (response.data.success) {
        // Account created successfully, redirect to dashboard
        window.location.href = '/dashboard';
      } else {
        setError(response.data.message || 'Failed to create account.');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'An error occurred while creating your account.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/register');
  };

  const getProviderIcon = (provider: string) => {
    switch (provider?.toLowerCase()) {
      case 'google':
        return '/assets/googleicon.svg';
      case 'twitch':
        return '/assets/twitchicon.svg';
      case 'kick':
        return '/assets/kickicon.svg';
      default:
        return null;
    }
  };

  const getProviderDisplayName = (provider: string) => {
    switch (provider?.toLowerCase()) {
      case 'google':
        return 'Google';
      case 'twitch':
        return 'Twitch';
      case 'kick':
        return 'Kick';
      default:
        return provider;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-black antialiased">
      <CardSpotlight className="w-full max-w-md">
        <div className="relative z-20">
          <div className="flex justify-center mb-8">
            <img src={clipoidLogo} alt="Clipoid Logo" className="h-14" />
          </div>
          
          <h1 className="text-3xl font-bold text-foreground mb-2 text-center">
            Create Your Account
          </h1>
          
          <p className="text-muted-foreground text-center mb-6">
            {message || `No account found for this ${getProviderDisplayName(provider || '')} login.`}
          </p>

          {error && (
            <div className="bg-red-500/10 border border-red-500 text-red-400 px-4 py-3 rounded-md w-full mb-4 text-sm" role="alert">
              {error}
            </div>
          )}

          {registrationData && (
            <div className="bg-secondary/50 rounded-lg p-4 mb-6">
              <div className="flex items-center mb-3">
                {registrationData.profileImageUrl ? (
                  <img
                    src={registrationData.profileImageUrl}
                    alt="Profile"
                    className="w-16 h-16 rounded-full mr-4 object-cover"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-muted mr-4 flex items-center justify-center">
                    <img 
                      src={getProviderIcon(registrationData.provider)} 
                      alt={`${getProviderDisplayName(registrationData.provider)} Icon`} 
                      className="w-8 h-8"
                    />
                  </div>
                )}
                <div>
                  <p className="font-semibold text-foreground">
                    {registrationData.name || registrationData.username || 'User'}
                  </p>
                  {registrationData.email && (
                    <p className="text-sm text-muted-foreground">{registrationData.email}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    via {getProviderDisplayName(registrationData.provider)}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <button
              onClick={handleCreateAccount}
              disabled={isLoading || !registrationData}
              className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold py-3 px-4 rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background focus:ring-primary disabled:opacity-60 disabled:cursor-not-allowed transition duration-300 ease-in-out transform hover:-translate-y-0.5 flex items-center justify-center"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-primary-foreground" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating Account...
                </>
              ) : (
                'Create Account'
              )}
            </button>

            <button
              onClick={handleCancel}
              disabled={isLoading}
              className="w-full border border-border text-foreground hover:bg-secondary/50 font-semibold py-3 px-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background focus:ring-border transition duration-150 ease-in-out"
            >
              Cancel
            </button>
          </div>

          <div className="mt-6 text-center text-xs text-muted-foreground">
            <p>By creating an account, you agree to Clipoid's{' '}
              <a href="/terms" target="_blank" className="font-medium text-primary hover:underline">
                Terms of Service
              </a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" className="font-medium text-primary hover:underline">
                Privacy Policy
              </a>.
            </p>
          </div>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center" aria-hidden="true">
              <div className="w-full border-t border-border" />
            </div>
          </div>

          <div className="text-center text-sm text-muted-foreground">
            <p>Already have an account?{' '}
              <a href="/login" className="font-medium text-primary hover:underline">
                Login instead
              </a>
            </p>
          </div>
        </div>
      </CardSpotlight>
    </div>
  );
}

export default OAuthRegisterPage;